/* eslint react/no-array-index-key: 0 */

import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@hover/blueprint';
import React, { useMemo, useState } from 'react';

interface CustomTableColumnProps<T, K extends keyof T> {
  isNumeric?: boolean;
  heading?: string;
  render: (row: T) => React.ReactNode;
  sortBy?: K;
}

interface CustomTableProps<T, K extends keyof T> {
  columns: Array<CustomTableColumnProps<T, K>>;
  data: Array<T>;
  defaultSortBy?: K;
}

const sortIcon = ({
  isSorted,
  order,
}: {
  isSorted: boolean;
  order?: 'asc' | 'desc';
}) => (
  <span
    style={{
      display: 'inline-block',
      marginLeft: 5,
    }}
  >
    <span
      style={{
        display: 'block',
        width: 0,
        height: 0,
        marginBottom: 3,
        borderBottom: '5px solid #030303',
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        opacity: isSorted && order === 'asc' ? 1 : 0.3,
      }}
    />

    <span
      style={{
        display: 'block',
        width: 0,
        height: 0,
        borderTop: '5px solid #030303',
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        opacity: isSorted && order === 'desc' ? 1 : 0.3,
      }}
    />
  </span>
);

const CustomTable = <T, K extends keyof T>({
  columns,
  data = [],
  defaultSortBy,
}: CustomTableProps<T, K>) => {
  const [sort, setSort] = useState<K | undefined>(defaultSortBy);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: CustomTableColumnProps<T, K>) => {
    if (!column.sortBy) {
      return;
    }

    if (sort === column.sortBy) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(column.sortBy);
      setOrder('asc');
    }
  };

  const sortedData: Array<T> = useMemo(() => {
    if (!sort) {
      return data;
    }

    const clonedData: Array<T> = [...data];
    return clonedData.sort((a: T, b: T) => {
      const aVal = (a[sort] || '').toString().toLowerCase();
      const bVal = (b[sort] || '').toString().toLowerCase();

      if (order === 'asc') {
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
      } else {
        if (aVal > bVal) return -1;
        if (aVal < bVal) return 1;
      }

      return 0;
    });
  }, [data, order, sort]);

  if (!sortedData || sortedData.length === 0) {
    return null;
  }

  return (
    <Box display="block" overflowX="auto">
      <Table>
        <Thead>
          {columns.map((column, index) => (
            <Th
              isNumeric={column.isNumeric}
              key={`th-${index}`}
              onClick={() => handleSort(column)}
              style={{
                cursor: column.sortBy ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
              }}
            >
              {column.heading}

              {column.sortBy &&
                sortIcon({ isSorted: column.sortBy === sort, order })}
            </Th>
          ))}
        </Thead>

        <Tbody>
          {sortedData.map((row, index) => (
            <Tr key={`tr-${index}`}>
              {columns.map((column, i) => (
                <Td isNumeric={column.isNumeric} key={`tr-${index}-td-${i}`}>
                  {column.render(row)}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export { CustomTable, CustomTableColumnProps };
